/**
 * PlanningRailPanel — the Planning Agent conversation, rendered inside the
 * left ChatRail. Hosts the compact gathers/status header (lifted from the
 * old BriefStatusStrip) directly above the BriefIntakeChat so the Draft
 * Canvas reclaims its vertical space.
 */
import { useEffect, useState } from "react";
import { AlertCircle, Check, CircleDashed, Eye, Folder, History, Layers, Loader2, MessageSquare, PenLine, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { tierHasCapability } from "@/lib/tier";
import { useWorkspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";
import { approveBriefPlan, clearApprovedBriefPlan, runBriefDraftPass } from "@/lib/composerCore";
import { composerBus } from "@/lib/composerBus";
import { BriefIntakeChat } from "./BriefIntakeChat";
import { listPieceInheritedScope, type ThreadScope } from "@/lib/pieceCorpusScope";
import { BUCKET_LABELS, type CorpusBucket } from "@/lib/workspaceCorpus";
import {
  GATHERS,
  readIntakeState,
  usePlanningContext,
  type ApprovedBrief,
  type IntakeChecklist,
} from "./planningContextCore";
import {
  listPlanSnapshots,
  restorePlanSnapshot,
  snapshotAndReset,
  summarizeSnapshot,
  type PlanningSnapshot,
} from "@/lib/planningSnapshots";
import {
  listPlanningSessions,
  writeActivePlanIdToHash,
  type PlanningSession,
} from "@/lib/planningSessions";
import { PlanningSessionSwitcher } from "./PlanningSessionSwitcher";



const Field = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-0.5">
    <div className="text-xs font-medium capitalize text-muted-foreground">{label}</div>
    <div className="whitespace-pre-wrap text-sm text-foreground">{value}</div>
  </div>
);

export const PlanningRailPanel = () => {
  const ctx = usePlanningContext();
  const { primaryTier: tier } = useWorkspace();
  const [approving, setApproving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<PlanningSnapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [inheritedScope, setInheritedScope] = useState<{
    docs: Array<{ id: string; title: string }>;
    folders: Array<{ id: string; name: string }>;
    buckets: CorpusBucket[];
  }>({ docs: [], folders: [], buckets: [] });
  const [sessions, setSessions] = useState<PlanningSession[]>([]);




  const pieceIdForScope = ctx?.brief?.id ?? null;
  const workspaceIdForScope = ctx?.workspaceId ?? null;
  useEffect(() => {
    if (!pieceIdForScope || !workspaceIdForScope) {
      setInheritedScope({ docs: [], folders: [], buckets: [] });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const scope: ThreadScope = await listPieceInheritedScope(workspaceIdForScope, pieceIdForScope);
        if (cancelled) return;
        if (
          scope.documentIds.length === 0 &&
          scope.folderIds.length === 0 &&
          scope.buckets.length === 0
        ) {
          setInheritedScope({ docs: [], folders: [], buckets: [] });
          return;
        }
        const [docsRes, foldersRes] = await Promise.all([
          scope.documentIds.length > 0
            ? supabase
                .from("workspace_corpus_documents")
                .select("id, title")
                .eq("workspace_id", workspaceIdForScope)
                .in("id", scope.documentIds)
            : Promise.resolve({ data: [] as Array<{ id: string; title: string | null }> }),
          scope.folderIds.length > 0
            ? supabase
                .from("workspace_corpus_folders")
                .select("id, name")
                .eq("workspace_id", workspaceIdForScope)
                .in("id", scope.folderIds)
            : Promise.resolve({ data: [] as Array<{ id: string; name: string | null }> }),
        ]);
        if (cancelled) return;
        setInheritedScope({
          docs: ((docsRes.data ?? []) as Array<{ id: string; title: string | null }>).map((r) => ({
            id: r.id,
            title: r.title ?? "Untitled",
          })),
          folders: ((foldersRes.data ?? []) as Array<{ id: string; name: string | null }>).map((r) => ({
            id: r.id,
            name: r.name ?? "Untitled",
          })),
          buckets: scope.buckets,
        });
      } catch {
        if (!cancelled) setInheritedScope({ docs: [], folders: [], buckets: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pieceIdForScope, workspaceIdForScope]);

  // Load Planning sessions for this brief. Refreshed whenever the piece
  // changes or the composer snapshot bumps `latestPlan` (new turn saved).
  const latestPlanIdForRefresh = ctx?.latestPlan?.id ?? null;
  const refreshSessions = async () => {
    if (!workspaceIdForScope || !pieceIdForScope) {
      setSessions([]);
      return;
    }
    try {
      const rows = await listPlanningSessions(workspaceIdForScope, pieceIdForScope);
      setSessions(rows);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load Planning sessions");
    }
  };
  useEffect(() => {
    void refreshSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceIdForScope, pieceIdForScope, latestPlanIdForRefresh]);





  if (!ctx || !ctx.brief || !ctx.workspaceId) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Open a Piece to start a Planning Agent conversation. The agent walks you through
          six gathers and proposes an approvable brief.
        </p>
      </div>
    );
  }

  const planningAllowed = tier ? tierHasCapability(tier, "content_planning_agent") : false;
  if (!planningAllowed) {
    return (
      <div className="flex h-full flex-col items-start gap-3 p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Planning Agent is on Plus and above.</p>
            <p className="text-xs text-muted-foreground">
              Walk through the six-gather intake one question at a time, then approve a brief
              the Writing Agent uses verbatim.
            </p>
          </div>
        </div>
        <Button size="sm" asChild>
          <a href="/pricing">View plans</a>
        </Button>
      </div>
    );
  }

  const { workspaceId, brief, latestPlan, liveBrief, onRefresh } = ctx;
  const { transcript, checklist: persistedChecklist, proposed: persistedProposed } = readIntakeState(latestPlan);
  const checklist = (liveBrief?.intake_checklist as IntakeChecklist | undefined) ?? persistedChecklist;
  const proposed = (liveBrief?.approved_brief as ApprovedBrief | null | undefined) ?? persistedProposed;
  const approvedJson = (brief as { approved_brief_json?: ApprovedBrief | null } | null)?.approved_brief_json ?? null;
  const hasApprovedSpec = Boolean(approvedJson);
  const isApproved = hasApprovedSpec && brief.status === "approved";
  const approvalNeedsSync = hasApprovedSpec && !isApproved;
  const completed = GATHERS.filter((g) => checklist[g.key]).length;
  const hasAnyActivity = transcript.length > 0 || proposed || hasApprovedSpec;

  const handleApprove = async () => {
    if (!proposed) return;
    setApproving(true);
    try {
      await approveBriefPlan(workspaceId, brief.id, { ...proposed, status: "APPROVED FULL BRIEF" });
      toast.success("Brief approved — Writing Agent will use this as authoritative");
      await onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Approval failed");
    } finally {
      setApproving(false);
    }
  };

  const handleUnapprove = async () => {
    setApproving(true);
    try {
      await clearApprovedBriefPlan(workspaceId, brief.id);
      toast.success("Approved brief cleared");
      await onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to clear");
    } finally {
      setApproving(false);
    }
  };

  const handleGenerateDraft = async () => {
    if (!approvedJson) return;
    setGenerating(true);
    try {
      if (brief.status !== "approved") {
        await approveBriefPlan(workspaceId, brief.id, approvedJson as unknown as Record<string, unknown>);
      }
      const result = await runBriefDraftPass(workspaceId, {
        briefId: brief.id,
        planId: latestPlan?.id ?? null,
      });
      toast.success("Draft generation started");
      await onRefresh();
      // Focus the freshly generated AI draft in the Draft Canvas so it doesn't
      // stay pinned to a prior user_edit revision (which would make the new
      // draft look "disappeared" even though its row exists in the drafts list).
      if (result?.draft?.id) {
        composerBus.emit("piece:draft:focus", {
          pieceId: brief.id,
          draftId: result.draft.id,
        });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to start draft");
    } finally {
      setGenerating(false);
    }
  };



  const handleReset = async () => {
    setResetting(true);
    try {
      if (latestPlan?.id) {
        // Snapshot the current intake_state into content_brief_plan_snapshots
        // before wiping it, so Restart is always reversible from the
        // "Snapshot history" sheet.
        await snapshotAndReset(latestPlan.id, "restart");
      }
      await clearApprovedBriefPlan(workspaceId, brief.id).catch(() => undefined);
      toast.success("Conversation reset — previous session saved to history");
      setResetOpen(false);
      setResetConfirmText("");
      await onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  const openHistory = async () => {
    if (!latestPlan?.id) {
      toast.info("No planning history yet on this piece.");
      return;
    }
    setHistoryOpen(true);
    setSnapshotsLoading(true);
    try {
      const rows = await listPlanSnapshots(latestPlan.id, 25);
      setSnapshots(rows);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load history");
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const handleRestore = async (snapshotId: string) => {
    setRestoringId(snapshotId);
    try {
      await restorePlanSnapshot(snapshotId);
      toast.success("Previous session restored");
      setHistoryOpen(false);
      await onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to restore");
    } finally {
      setRestoringId(null);
    }
  };


  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Session switcher — multi-thread parity with AI Chat. */}
      <div className="flex items-center gap-2 border-b border-border/60 px-3 py-1.5">
        <PlanningSessionSwitcher
          workspaceId={workspaceId}
          briefId={brief.id}
          sessions={sessions}
          activePlanId={latestPlan?.id ?? null}
          onSelect={(planId) => {
            writeActivePlanIdToHash(planId);
            void onRefresh();
          }}
          onChanged={async () => {
            await refreshSessions();
            await onRefresh();
          }}
          onDeleted={async (deletedId) => {
            // If the deleted session was active, drop the hash so we fall
            // back to the newest surviving session.
            if (latestPlan?.id === deletedId) writeActivePlanIdToHash(null);
            await refreshSessions();
            await onRefresh();
          }}
        />
      </div>
      {/* Compact gathers + actions header */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2">

        {isApproved ? (
          <Badge variant="default" className="gap-1">
            <Check className="h-3 w-3" /> Approved
          </Badge>
        ) : approvalNeedsSync ? (
          <Badge variant="secondary" className="text-[10px]">
            Needs sync
          </Badge>
        ) : hasAnyActivity ? (
          <Badge variant="secondary" className="text-[10px]">
            {completed} / {GATHERS.length}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">Not started</Badge>
        )}

        <div className="flex items-center gap-1">
          {GATHERS.map((g) => {
            const done = Boolean(checklist[g.key]);
            return (
              <Tooltip key={g.key}>
                <TooltipTrigger asChild>
                  <span aria-label={g.label} className="inline-flex">
                    {done ? (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <CircleDashed className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">{g.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-1">
          {proposed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => setViewerOpen(true)}
                  aria-label="View proposed brief"
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">View proposed brief</TooltipContent>
            </Tooltip>
          ) : null}

          {isApproved || approvalNeedsSync ? (
            <>
              <Button
                size="sm"
                onClick={handleGenerateDraft}
                disabled={generating || approving}
                className="h-7 gap-1 text-xs"
              >
                {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PenLine className="h-3.5 w-3.5" />}
                Generate draft
              </Button>
              <Button size="sm" variant="ghost" onClick={handleUnapprove} disabled={approving || generating} className="h-7 text-xs">
                Clear approval
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={handleApprove} disabled={!proposed || approving} className="h-7 gap-1 text-xs">
              {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Approve
            </Button>
          )}


          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={openHistory}
                disabled={!latestPlan?.id}
                aria-label="View planning session history"
              >
                <History className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">Snapshot history</TooltipContent>
          </Tooltip>

          <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>

            <AlertDialogTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                disabled={!hasAnyActivity}
                title="Restart conversation"
                aria-label="Restart Planning Agent conversation"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Restart Planning Agent conversation?</AlertDialogTitle>
              
              <AlertDialogDescription>
                This clears the current transcript, proposed brief, and (if approved) the approved
                brief on this Piece so you can start a fresh Planning Agent conversation. The
                previous session is preserved in <strong>Snapshot history</strong> and can be
                restored at any time. Type <strong>restart</strong> to confirm.
              </AlertDialogDescription>

              </AlertDialogHeader>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="restart"
                autoFocus
              />
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setResetConfirmText("")}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={resetConfirmText.trim().toLowerCase() !== "restart" || resetting}
                  onClick={handleReset}
                >
                  {resetting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                  Restart
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Corpus scope — inherited from AI Chat threads on this Piece.
          Planner + Writer narrow their retrieval to this union on every run.
          Read-only here: scope in the AI Chat rail (Scope chip or `/`). */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border/60 px-3 py-1.5">
        <Search className="h-3 w-3 text-muted-foreground" />
        <span className="text-[11px] font-medium text-muted-foreground">
          Scoped from AI Chat:
        </span>
        {inheritedScope.docs.length === 0 &&
        inheritedScope.folders.length === 0 &&
        inheritedScope.buckets.length === 0 ? (
          <span className="text-[11px] text-muted-foreground/70">
            None — the whole Corpus is searchable. Scope in AI Chat (
            <MessageSquare className="inline h-3 w-3" /> rail) to narrow the Planner &amp; Writer.
          </span>
        ) : (
          <>
            {inheritedScope.buckets.map((b) => (
              <Badge key={`b-${b}`} variant="secondary" className="max-w-[180px] gap-1 truncate text-[10px]">
                <Layers className="h-3 w-3" />
                {BUCKET_LABELS[b]}
              </Badge>
            ))}
            {inheritedScope.folders.map((f) => (
              <Badge key={`f-${f.id}`} variant="secondary" className="max-w-[180px] gap-1 truncate text-[10px]">
                <Folder className="h-3 w-3" />
                {f.name}
              </Badge>
            ))}
            {inheritedScope.docs.map((d) => (
              <Badge key={`d-${d.id}`} variant="secondary" className="max-w-[180px] truncate text-[10px]">
                {d.title}
              </Badge>
            ))}
          </>
        )}
      </div>

      {/* Empty-pins warning — the Planning Agent approved a brief but pinned
          zero Corpus documents. Non-blocking. Fires whether the approval is
          the current UI state or an older approved_brief_json that predates
          the search_corpus / list_scoped_corpus_docs tools. Restart or
          re-approve after the planner surfaces ids. */}
      {(() => {
        const approvedIds = ((approvedJson ?? proposed) as (ApprovedBrief & { corpus_document_ids?: unknown }) | null)
          ?.corpus_document_ids;
        const idCount = Array.isArray(approvedIds) ? approvedIds.length : 0;
        const showWarning = (hasApprovedSpec || Boolean(proposed)) && idCount === 0;
        if (!showWarning) return null;
        return (
          <div className="flex items-start gap-2 border-b border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-900 dark:text-amber-200">
            <AlertCircle className="mt-0.5 h-3 w-3 flex-none" />
            <span>
              No Corpus documents are pinned on this brief — the Writing Agent will search
              freely instead of locking onto specific sources. If the outline names a specific
              book, transcript, or essay, restart the Planning Agent and ask it to pin those
              sources, or scope them in the AI Chat first.
            </span>
          </div>
        );
      })()}





      {/* Chat */}
      <div className="min-h-0 flex-1 p-2">
        <BriefIntakeChat
          key={latestPlan?.id ?? brief.id}
          workspaceId={workspaceId}
          pieceId={brief.id}
          planId={latestPlan?.id ?? null}
          initialMessages={transcript}
          onTurnFinish={onRefresh}
          onLiveBriefChange={ctx.setLiveBrief}
          onBeforeMutateApproved={ctx.handleBeforeMutateApproved}
        />
      </div>

      <Sheet open={viewerOpen} onOpenChange={setViewerOpen}>
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Proposed brief</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3 text-sm">
            {proposed ? (
              <>
                {proposed.topic ? <Field label="Topic" value={proposed.topic} /> : null}
                {proposed.core_message ? <Field label="Core message" value={proposed.core_message} /> : null}
                {proposed.real_life_anchor ? <Field label="Real-life anchor" value={proposed.real_life_anchor} /> : null}
                {proposed.publish_type_key ? <Field label="Publish type" value={proposed.publish_type_key} /> : null}
                <Field label="Include Excerpts" value={proposed.include_excerpt_retrieval ? "Yes" : "No"} />
                {proposed.outline ? (
                  <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Outline</div>
                    {(["hook", "intro", "body", "conclusion"] as const).map((k) =>
                      proposed.outline?.[k] ? <Field key={k} label={k} value={proposed.outline[k] as string} /> : null,
                    )}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-muted-foreground">No proposed brief yet.</p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Planning session history</SheetTitle>
          </SheetHeader>
          <p className="mt-2 text-xs text-muted-foreground">
            Every time the Planning Agent overwrites its state (regular save, restart, or
            restore), the previous conversation is snapshotted here. Restoring a snapshot
            replaces the current session — the current session itself is snapshotted first,
            so restore is reversible.
          </p>
          <div className="mt-4 space-y-2">
            {snapshotsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading history…
              </div>
            ) : snapshots.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No snapshots yet. Every future save or restart on this piece is captured here.
              </p>
            ) : (
              snapshots.map((snap) => {
                const { turnCount, hasProposed } = summarizeSnapshot(snap);
                const when = new Date(snap.created_at).toLocaleString();
                const reasonLabel =
                  snap.reason === "restart"
                    ? "before Restart"
                    : snap.reason === "pre_restore"
                    ? "before Restore"
                    : "before turn";
                return (
                  <div
                    key={snap.id}
                    className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/20 p-3"
                  >
                    <div className="min-w-0 flex-1 space-y-0.5 text-xs">
                      <div className="font-medium text-foreground">{when}</div>
                      <div className="text-muted-foreground">
                        {turnCount} turn{turnCount === 1 ? "" : "s"}
                        {hasProposed ? " · proposed brief present" : ""} · {reasonLabel}
                      </div>
                      {snap.brief_summary ? (
                        <div className="line-clamp-2 pt-1 text-muted-foreground">
                          {snap.brief_summary}
                        </div>
                      ) : null}
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={restoringId === snap.id}
                      onClick={() => handleRestore(snap.id)}
                    >
                      {restoringId === snap.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Restore"
                      )}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>

    </div>
  );
};

export default PlanningRailPanel;
